/* eslint-disable unicode-bom */
// apolice table (clean file without BOM)
import React, { useEffect, useState } from 'react';
import { apoliceService, ApoliceData, ApoliceSearchParams, getApoliceProdutoNome, getApoliceSeguradoraNome, getApoliceClienteNome } from '../../services/apoliceService';
import { formatDate } from '../../utils/formatters';
import { seguradoraService, produtoService, situacaoService, empresaService, statusService } from '../../services/entitiesServices';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface Props {
  onEdit: (a: ApoliceData) => void;
  onView?: (a: ApoliceData) => void;
  onRefresh: () => void;
  onDelete?: (a: ApoliceData) => void;
  // id of an active apólice to highlight/scroll to (optional)
  activeId?: string | null;
  // register a navigator function so parent can ask for next record by id
  // navigator may fetch the next page; it returns a Promise resolving to the next ApoliceData or undefined
  onRegisterNavigator?: (fn: (currentId?: string) => Promise<ApoliceData | undefined>) => void;
  // trigger to refresh the table
  refreshTrigger?: number;
}

export const ApoliceTable: React.FC<Props> = ({ onEdit, onView, onRefresh, onDelete, onRegisterNavigator, activeId, refreshTrigger }) => {
  const [items, setItems] = useState<ApoliceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('inicioVigencia');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [apoliceToDelete, setApoliceToDelete] = useState<ApoliceData | null>(null);

  // Filter states
  const [filters, setFilters] = useState<NonNullable<ApoliceSearchParams['filters']>>({});
  const [tempFilters, setTempFilters] = useState<NonNullable<ApoliceSearchParams['filters']>>({});
  const [showFilters, setShowFilters] = useState(false);
  
  // Catalog data for dropdowns
  const [seguradoras, setSeguradoras] = useState<any[]>([]);
  const [produtos, setProdutos] = useState<any[]>([]);
  const [situacoes, setSituacoes] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);

  const loadApolices = async (params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc'; filters?: ApoliceSearchParams['filters'] }) => {
    setLoading(true);
    try {
      let q = (params?.search || '').trim();
      
  const currentSortBy = params?.sortBy || sortBy;
  const currentSortOrder = params?.sortOrder || sortOrder;
  const currentFilters = params?.filters || filters;
      const resp = await apoliceService.getApolices({ 
        search: q || undefined, 
        page: params?.page || page, 
        limit: params?.limit || limit, 
        sortBy: currentSortBy,
        sortOrder: currentSortOrder,
        filters: currentFilters,
      }, '/documentos');
      
      setItems(resp.data || []);
      setTotalPages(resp.totalPages);
      

      
      // register navigator after items load
      if (onRegisterNavigator) {
  // capture page for this load
  const currentPage = params?.page || page;

        const fn = async (currentId?: string) => {
          const list = resp.data || [];
          if (!currentId) return list.length > 0 ? list[0] : undefined;
          const idx = list.findIndex((x: ApoliceData) => x.id === currentId);
          // if next item exists on the same page, return it
          if (idx >= 0 && idx + 1 < list.length) return list[idx + 1];
          // if this is the last item on current page AND there are more pages, load next page
          if (idx >= 0 && idx === list.length - 1 && resp.totalPages && currentPage < resp.totalPages) {
            // trigger table to load next page
            const nextPageNum = currentPage + 1;
            setTimeout(() => handlePageChange(nextPageNum), 50); // small delay to close modal first
            // return undefined to close modal - table will show next page
            return undefined;
          }
          // if we're at the last item of the last page, just close modal
          return undefined;
        };
        onRegisterNavigator(fn);
      }
    } catch (e) {
      console.error('❌ APOLICE ERROR:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    // toggle direction when clicking the same field
    if (field === sortBy) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      setPage(1);
      loadApolices({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: newOrder, filters });
    } else {
      setSortBy(field);
      setSortOrder('asc');
      setPage(1);
      loadApolices({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: 'asc', filters });
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) {
      // neutral icon (up/down faint)
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 9l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 15l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (sortOrder === 'asc') {
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 15l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    return (
      <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  // Render an icon for the `situacao` field based on its name
  const renderSituacaoIcon = (situacaoNome?: string) => {
    const name = (situacaoNome || '').toLowerCase();
    if (name.includes('ativa') || name.includes('ativo') || name.includes('aprov')) {
      // green check-circle
      return (
        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        </svg>
      );
    }
    if (name.includes('cancel') || name.includes('cancelada') || name.includes('cancelado')) {
      // red x-circle
      return (
        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        </svg>
      );
    }
    if (name.includes('pend') || name.includes('aguard') || name.includes('em aberto')) {
      // yellow clock / pending
      return (
        <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        </svg>
      );
    }
    // default info / gray
    return (
      <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z" />
      </svg>
    );
  };

  // Load catalog data for filters
  const loadCatalogs = async () => {
    try {
      // Load catalogs with individual error handling - use high limit to get all records
      const results = await Promise.allSettled([
        statusService.list({ limit: 1000 }),
        seguradoraService.list({ limit: 1000 }),
        produtoService.list({ limit: 1000 }),
        situacaoService.list({ limit: 1000 }),
        empresaService.list({ limit: 1000 })
      ]);

      // Process results with proper error handling and fallbacks
      let statusesData = results[0].status === 'fulfilled' ? results[0].value : { data: [] };
      const seguradorasData = results[1].status === 'fulfilled' ? results[1].value : { data: [] };
      const produtosData = results[2].status === 'fulfilled' ? results[2].value : { data: [] };
      const situacoesData = results[3].status === 'fulfilled' ? results[3].value : { data: [] };
      const empresasData = results[4].status === 'fulfilled' ? results[4].value : { data: [] };

      // If status loading failed, try to provide default status options
      if (results[0].status === 'rejected' || !statusesData.data || statusesData.data.length === 0) {
        console.warn('Status data not available, using default options');
        statusesData = {
          data: [
            { id: '1', nome: 'Proposta' },
            { id: '2', nome: 'Apólice' },
            { id: '3', nome: 'Cancelado' }
          ]
        } as any;
      }

      // Log any failed requests
      results.forEach((result, index) => {
        const names = ['statuses', 'seguradoras', 'produtos', 'situacoes', 'empresas'];
        if (result.status === 'rejected') {
          console.error(`Error loading ${names[index]}:`, result.reason);
        }
      });
      
      setStatuses(statusesData.data || []);
      setSeguradoras(seguradorasData.data || []);
      setProdutos(produtosData.data || []);
      setSituacoes(situacoesData.data || []);
      setEmpresas(empresasData.data || []);
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  };

  useEffect(() => {
    loadCatalogs();
    loadApolices({ page: 1, limit: limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadApolices({ page: 1, limit: limit, search: searchTerm, filters });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadApolices({ page: newPage, limit: limit, search: searchTerm, filters });
  };

  const handleTempFilterChange = (key: keyof NonNullable<ApoliceSearchParams['filters']>, value: string | undefined) => {
    const newTempFilters = { ...tempFilters, [key]: value };
    setTempFilters(newTempFilters);
  };

  const handleTempDateFilterChange = (dateField: 'dataEmissao' | 'fimVigencia' | 'inicioVigencia', rangeType: 'gte' | 'lte', value: string) => {
    const newTempFilters = {
      ...tempFilters,
      [dateField]: {
        ...(tempFilters[dateField] || {}),
        [rangeType]: value || undefined
      }
    };
    // Clean up empty date objects
    if (!newTempFilters[dateField]?.gte && !newTempFilters[dateField]?.lte) {
      delete newTempFilters[dateField];
    }
    setTempFilters(newTempFilters);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    loadApolices({ page: 1, limit: limit, search: searchTerm, filters: tempFilters });
  };

  const clearFilters = () => {
    setFilters({});
    setTempFilters({});
    setPage(1);
    loadApolices({ page: 1, limit: limit, search: searchTerm, filters: {} });
  };

  // scroll active row into view when items change
  useEffect(() => {
    if (!activeId) return;
    // find DOM element by data-active attribute
    const el = document.querySelector(`[data-apolice-id="${activeId}"]`);
    if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [items, activeId]);

  // refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadApolices({ page, limit, search: searchTerm, sortBy, sortOrder, filters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  if (loading) return <div className="p-3 text-center text-xs">Carregando apólices...</div>;

  return (
    <div className="space-y-3">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Apólices ({items.length})
        </h2>
        
        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="status.nome">Status</option>
              <option value="situacao.nome">Situação</option>
              <option value="inicioVigencia">Início Vigência</option>
              <option value="numeroDocumento">Documento</option>
              <option value="numeroApolice">Apólice</option>
              <option value="numeroEndosso">Endosso</option>
              <option value="cliente.nome">Cliente</option>
              <option value="seguradora.nome">Seguradora</option>
              <option value="produto.nome">Produto</option>
            </select>
            <button
              onClick={() => {
                const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(newOrder);
                setPage(1);
                loadApolices({ page: 1, limit, search: searchTerm, sortBy, sortOrder: newOrder, filters });
              }}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar apólices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '160px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
          >
            Filtros {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 p-3 rounded-md border filters-panel" style={{ borderColor: '#C3B4DE', overflow: 'visible', position: 'relative' }}>
          <style>{`
            .filter-select {
              -webkit-appearance: menulist !important;
              -moz-appearance: menulist !important;
              appearance: auto !important;
              background-image: none !important;
              background-color: white !important;
            }
            .filters-panel {
              overflow: visible !important;
              position: relative !important;
            }
            .filter-select:focus {
              outline: none !important;
              border-color: #7A61B2 !important;
              box-shadow: 0 0 0 1px #7A61B2 !important;
            }
            .filter-select option {
              padding: 4px 8px !important;
              font-size: 12px !important;
              background: white !important;
              color: black !important;
            }
          `}</style>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            
            {/* Status */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Status
              </label>
              <select
                value={tempFilters.statusId || ''}
                onChange={(e) => handleTempFilterChange('statusId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todos</option>
                {statuses.length > 0 ? statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                )) : (
                  <option disabled>Carregando status...</option>
                )}
              </select>
            </div>

            {/* Situação */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Situação
              </label>
              <select
                value={tempFilters.situacaoId || ''}
                onChange={(e) => handleTempFilterChange('situacaoId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todas</option>
                {situacoes.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>

            {/* Seguradora */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Seguradora
              </label>
              <select
                value={tempFilters.seguradoraId || ''}
                onChange={(e) => handleTempFilterChange('seguradoraId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todas</option>
                {seguradoras.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>

            {/* Produto */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Produto
              </label>
              <select
                value={tempFilters.produtoId || ''}
                onChange={(e) => handleTempFilterChange('produtoId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todos</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>{p.nome}</option>
                ))}
              </select>
            </div>

            {/* Data Emissão - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Data Emissão
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">De</label>
                  <input
                    type="date"
                    value={tempFilters.dataEmissao?.gte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataEmissao', 'gte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Até</label>
                  <input
                    type="date"
                    value={tempFilters.dataEmissao?.lte || ''}
                    onChange={(e) => handleTempDateFilterChange('dataEmissao', 'lte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
              </div>
            </div>

            {/* Início Vigência - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Início Vigência
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">De</label>
                  <input
                    type="date"
                    value={tempFilters.inicioVigencia?.gte || ''}
                    onChange={(e) => handleTempDateFilterChange('inicioVigencia', 'gte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Até</label>
                  <input
                    type="date"
                    value={tempFilters.inicioVigencia?.lte || ''}
                    onChange={(e) => handleTempDateFilterChange('inicioVigencia', 'lte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
              </div>
            </div>

            {/* Fim Vigência - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Fim Vigência
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">De</label>
                  <input
                    type="date"
                    value={tempFilters.fimVigencia?.gte || ''}
                    onChange={(e) => handleTempDateFilterChange('fimVigencia', 'gte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Até</label>
                  <input
                    type="date"
                    value={tempFilters.fimVigencia?.lte || ''}
                    onChange={(e) => handleTempDateFilterChange('fimVigencia', 'lte', e.target.value)}
                    className="w-full px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
                    style={{ borderColor: '#C3B4DE' }}
                  />
                </div>
              </div>
            </div>

            {/* Empresa - span full width */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: '#3d2b56' }}>
                Empresa
              </label>
              <select
                value={tempFilters.empresaId || ''}
                onChange={(e) => handleTempFilterChange('empresaId', e.target.value || undefined)}
                className="filter-select"
                style={{ 
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #C3B4DE',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Todas</option>
                {empresas.map((e) => (
                  <option key={e.id} value={e.id}>{e.nome}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={clearFilters}
              className="px-3 py-1 text-xs font-medium border rounded-md hover:bg-gray-100"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
            >
              Limpar Filtros
            </button>
            <button
              onClick={applyFilters}
              className="px-3 py-1 text-xs font-medium border rounded-md text-white hover:opacity-90"
              style={{ backgroundColor: '#7A61B2', borderColor: '#7A61B2' }}
            >
              Buscar
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-6">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            Carregando apólices...
          </div>
        </div>
      )}

      {/* Tabela de apólices */}
      {!loading && items.length > 0 && (
        <>
          {/* Cards Grid */}
          <div className="space-y-2">
            {items.map((it, index) => {
              // Determinar cor da borda baseada no status
              let borderColor = '#10B981'; // Verde padrão para Apólice
              const statusNome = it.status?.nome || (typeof it.status === 'string' ? it.status : '');
              
              if (statusNome.toLowerCase().includes('proposta')) {
                borderColor = '#C3B4DE'; // Roxo claro para Proposta
              } else if (statusNome.toLowerCase().includes('endosso')) {
                borderColor = '#059669'; // Verde mais escuro para Endosso
              } else if (statusNome.toLowerCase().includes('apolice') || statusNome.toLowerCase().includes('apólice')) {
                borderColor = '#10B981'; // Verde para Apólice
              }
              
              return (
                <div 
                  key={it.id} 
                  data-apolice-id={it.id} 
                  className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow relative overflow-hidden ${activeId === it.id ? 'ring-2 ring-indigo-300' : ''}`}
                  style={{ borderLeft: `4px solid ${borderColor}` }}
                >
                  <div className="p-3">
                    {/* Header with Status and Actions */}
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          it.status?.nome === 'Apólice' ? 'bg-green-100 text-green-800' :
                          it.status?.nome === 'Proposta' ? 'bg-purple-100 text-purple-800' :
                          it.status?.nome === 'Endosso' ? 'bg-green-100 text-green-900' :
                          it.status?.nome === 'Cancelado' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {it.status?.nome || (typeof it.status === 'string' ? it.status : '-')}
                        </span>
                        <span className="text-sm text-gray-600">
                          {it.situacao?.nome || (typeof it.situacao === 'string' ? it.situacao : '-')}
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-1">
                        {onView && (
                          <button 
                            onClick={() => onView(it)} 
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded transition-colors" 
                            title="Ver Detalhes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        <button 
                          onClick={() => onEdit(it)} 
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 p-1 rounded transition-colors"
                          title="Editar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button 
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors" 
                          title="Excluir"
                          onClick={() => {
                            setApoliceToDelete(it);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  {/* Left Column */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Proposta</div>
                        <div className="text-sm font-semibold text-gray-900 font-mono leading-tight">{it.numeroDocumento || '-'}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                        <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{it.cliente?.nome || '-'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Left Column */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Seguradora</div>
                        <div className="text-sm text-gray-700 leading-tight">{it.seguradora?.nome || (typeof it.seguradora === 'string' ? it.seguradora : '-')}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produto</div>
                        <div className="text-sm text-gray-700 leading-tight">{it.produto?.nome || (typeof it.produto === 'string' ? it.produto : '-')}</div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Right Column */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Vigência</div>
                        <div className="text-sm text-gray-700 leading-tight">{it.inicioVigencia ? formatDate(it.inicioVigencia) : (it.dataEmissao ? formatDate(it.dataEmissao) : '-')}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      {renderSituacaoIcon(it.situacao?.nome || (typeof it.situacao === 'string' ? it.situacao : '-'))}
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Situação</div>
                        <div className="text-sm text-gray-700 leading-tight">{it.situacao?.nome || (typeof it.situacao === 'string' ? it.situacao : '-')}</div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3">
                    {(it.numeroApolice || it.numeroEndosso) && (
                      <>
                        {it.numeroApolice && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Apólice</span>
                            <p className="text-sm text-gray-700 font-mono leading-tight">{it.numeroApolice}</p>
                          </div>
                        )}

                        {it.numeroEndosso && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Endosso</span>
                            <p className="text-sm text-gray-700 font-mono leading-tight">{it.numeroEndosso}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Paginação */}
      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            {totalPages ? `Página ${page} de ${totalPages}` : `Mostrando ${items.length} apólices`}
          </div>
          
          <div className="flex space-x-1">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Anterior
            </button>
            
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
              Página {page}{totalPages ? ` de ${totalPages}` : ''}
            </span>
            
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={totalPages ? page >= totalPages : items.length < limit}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Mensagem quando não há dados */}
      {!loading && items.length === 0 && (
        <div className="text-center py-8">
          <p className="text-xs" style={{ color: '#9E9E9E' }}>
            {searchTerm ? 'Nenhuma apólice encontrada para a busca.' : 'Nenhuma apólice cadastrada.'}
          </p>
        </div>
      )}

      {/* Dialog de confirmação de exclusão */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir Documento"
        message="Tem certeza que deseja excluir este documento?"
        details={apoliceToDelete ? [
          `Nº: ${apoliceToDelete.numeroDocumento || apoliceToDelete.numeroApolice || 'sem número'}`,
          `Cliente: ${getApoliceClienteNome(apoliceToDelete)}`,
          `Produto: ${getApoliceProdutoNome(apoliceToDelete)}`
        ] : []}
        onConfirm={() => {
          if (onDelete && apoliceToDelete) {
            onDelete(apoliceToDelete);
            setShowDeleteConfirm(false);
            setApoliceToDelete(null);
          }
        }}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setApoliceToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="#DC2626"
      />
    </div>
  );
};